import React, { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { Button, Form } from "react-bootstrap";
import { ORIGIN } from "../utils/configs";
import { processCommand } from "../utils/utils";
import {
  buildOpenPositionCommand,
  buildLiquidityCatchCommand,
  buildPositionCloseCommand,
  buildTpSlCommand,
  formatPnl,
  formatTradePrice,
  LIQUIDITY_CATCH_CANDLE_LEVELS,
  LIQUIDITY_CATCH_EXPIRIES,
  liquidityCatchCandleTimestamp,
  normalizeLiquidityCatchTimeframe,
  normalizeEntryProtectionMode,
  resolveLiquidityCatchCandleLevel,
  quickAddLabel
} from "./tradeUtils";
import TradeConfirmationModal from "./TradeConfirmationModal";
import EntryProtectionSelector from "../components/popups/forms/fields/EntryProtectionSelector";
import "./TradingPanel.css";

const fetcher = (...args) => fetch(...args, { credentials: "same-origin", cache: "no-store" }).then((res) => res.json());
const ADD_SIZES = [5, 20, 100];
const REDUCE_PCTS = [25, 50, 100];
const PROTECT_PCTS = [25, 50, 100];
const LEVERAGES = [1, 2, 3, 4, 5, 10, 20];

function normalizeSide(side, positionAmt) {
  const raw = String(side || "").toUpperCase();
  if (raw === "LONG" || raw === "SHORT") return raw;
  const amt = Number(positionAmt);
  if (Number.isFinite(amt) && amt !== 0) return amt > 0 ? "LONG" : "SHORT";
  return "LONG";
}

function laneSide(lane) {
  return normalizeSide(lane?.positionSide, lane?.positionAmt);
}

function activeOrderPrice(order) {
  return order?.stopPrice ?? order?.price;
}

function positionText(lane) {
  if (!lane) return "No active exchange position";
  const qty = Math.abs(Number(lane.positionAmt));
  const notional = Math.abs(Number(lane.notional));
  const parts = [];
  if (Number.isFinite(qty)) parts.push(`${qty.toFixed(qty >= 10 ? 3 : 5).replace(/\.?0+$/, "")} qty`);
  if (Number.isFinite(notional)) parts.push(`${notional.toFixed(2)} USDT`);
  if (lane.leverage) parts.push(`x${lane.leverage}`);
  return parts.join(" / ");
}

function orderSummary(orders, type) {
  const wanted = type === "StopLoss" ? "STOP" : "TAKE_PROFIT";
  return (orders || []).find((order) => String(order?.type || "").toUpperCase().includes(wanted));
}

function selectedCandleDateLabel(selectedCandle) {
  const timestamp = liquidityCatchCandleTimestamp(selectedCandle?.selectedCandle, selectedCandle?.date);
  return timestamp == null ? "Time unavailable" : new Date(timestamp).toLocaleString();
}

function savedCatchPreferences() {
  try {
    return JSON.parse(localStorage.getItem("catchTradeData") || "{}");
  } catch (error) {
    return {};
  }
}

export default function TradingPanel({
  currency, timeframe, selectedEvent, selectedCandle, mobile = false, open, onClose,
  initialSide, initialEntryPrice, initialStopPrice, initialTakePrice,
}) {
  const symbol = currency ? `${String(currency).toUpperCase()}USDT` : null;
  const positionJournalUrl = open && symbol
    ? `${ORIGIN}/insights/account/positions`
    : null;
  const { data: positionJournal, mutate } = useSWR(positionJournalUrl, fetcher, {
    refreshInterval: open ? 20000 : 0,
    revalidateOnFocus: true
  });
  const currentPositionJournal = positionJournal?.status === "CURRENT" ? positionJournal : null;
  const lanes = useMemo(() => (currentPositionJournal?.lanes || [])
    .filter((lane) => String(lane.symbol || "").toUpperCase() === symbol), [currentPositionJournal, symbol]);
  const [selectedSide, setSelectedSide] = useState("LONG");
  const [amountUsdt, setAmountUsdt] = useState(20);
  const [reducePercent, setReducePercent] = useState(25);
  const [stopPercent, setStopPercent] = useState(100);
  const [takePercent, setTakePercent] = useState(100);
  const [entryPrice, setEntryPrice] = useState("");
  const [stopPrice, setStopPrice] = useState("");
  const [takePrice, setTakePrice] = useState("");
  const [leverage, setLeverage] = useState(10);
  const [status, setStatus] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [panelMode, setPanelMode] = useState("MANUAL");
  const [catchBaseline, setCatchBaseline] = useState("");
  const [catchPreferences] = useState(savedCatchPreferences);
  const [catchStopPercent, setCatchStopPercent] = useState(catchPreferences.stopLossPercent || 1);
  const [catchTp1Percent, setCatchTp1Percent] = useState(catchPreferences.tp1Percent || 2);
  const [catchTp2Percent, setCatchTp2Percent] = useState(catchPreferences.tp2Percent || "HOLD");
  const [catchExpiry, setCatchExpiry] = useState(catchPreferences.catchExpiry || 24);
  const [catchEntryProtectionMode, setCatchEntryProtectionMode] = useState(
    normalizeEntryProtectionMode(catchPreferences.entryProtectionMode, catchPreferences.protectEntry)
  );
  const [catchBaselineSource, setCatchBaselineSource] = useState("MANUAL");
  const [catchBaselineSelector, setCatchBaselineSelector] = useState("LOW");

  const catchTimeframe = normalizeLiquidityCatchTimeframe(timeframe);

  useEffect(() => {
    if (!open || (!initialSide && !initialEntryPrice && !initialStopPrice && !initialTakePrice)) return;
    setSelectedSide(normalizeSide(initialSide));
    setEntryPrice(Number.isFinite(Number(initialEntryPrice)) && Number(initialEntryPrice) > 0
      ? formatTradePrice(initialEntryPrice) : "");
    setStopPrice(Number.isFinite(Number(initialStopPrice)) && Number(initialStopPrice) > 0
      ? formatTradePrice(initialStopPrice) : "");
    setTakePrice(Number.isFinite(Number(initialTakePrice)) && Number(initialTakePrice) > 0
      ? formatTradePrice(initialTakePrice) : "");
    setPanelMode("MANUAL");
    setStatus(initialEntryPrice || initialStopPrice || initialTakePrice
      ? "Prepared from the saved AI assessment" : "Prepared for the saved assessment side");
  }, [initialEntryPrice, initialSide, initialStopPrice, initialTakePrice, open, symbol]);

  useEffect(() => {
    if (lanes.length === 0) return;
    const firstSide = laneSide(lanes[0]);
    setSelectedSide((prev) => lanes.some((lane) => laneSide(lane) === prev) ? prev : firstSide);
  }, [lanes]);

  const selectedLane = useMemo(() => lanes.find((lane) => laneSide(lane) === selectedSide), [lanes, selectedSide]);
  const selectedOrders = useMemo(() => (currentPositionJournal?.orders || []).filter((order) => (
    String(order.symbol || "").toUpperCase() === symbol
      && normalizeSide(order.positionSide, order.positionAmt) === selectedSide
  )), [currentPositionJournal, selectedSide, symbol]);
  const stopOrder = orderSummary(selectedOrders, "StopLoss");
  const takeOrder = orderSummary(selectedOrders, "TakeProfit");

  useEffect(() => {
    if (selectedLane?.leverage) setLeverage(selectedLane.leverage);
  }, [selectedLane?.leverage]);

  useEffect(() => {
    const onPricePick = (event) => {
      const detail = event.detail || {};
      if (detail.symbol && symbol && String(detail.symbol).toUpperCase() !== symbol) return;
      const price = Number(detail.price);
      if (!Number.isFinite(price)) return;
      const formatted = formatTradePrice(price);
      if (detail.field === "stopLoss") {
        setStopPrice(formatted);
        setStatus(`Picked SL ${formatted}`);
      } else if (detail.field === "takeProfit") {
        setTakePrice(formatted);
        setStatus(`Picked TP ${formatted}`);
      } else if (detail.field === "entry") {
        if (panelMode === "CATCH") {
          setCatchBaseline(formatted);
          setCatchBaselineSource("MANUAL");
          setCatchBaselineSelector("PRICE");
          setStatus(`Picked Catch baseline ${formatted}`);
        } else {
          setEntryPrice(formatted);
          setStatus(`Picked entry ${formatted}`);
        }
      }
    };
    window.addEventListener("trading_panel_price_pick", onPricePick);
    return () => window.removeEventListener("trading_panel_price_pick", onPricePick);
  }, [panelMode, symbol]);

  useEffect(() => {
    if (panelMode !== "CATCH" || catchBaseline) return;
    const eventPrice = Number(selectedEvent?.price);
    if (Number.isFinite(eventPrice) && eventPrice > 0) {
      setCatchBaseline(formatTradePrice(eventPrice));
      setCatchBaselineSource("MANUAL");
      setCatchBaselineSelector("PRICE");
      setStatus("Baseline initialized from the selected event price");
    }
  }, [catchBaseline, panelMode, selectedEvent?.price]);

  useEffect(() => {
    const candle = selectedCandle?.selectedCandle;
    if (!candle) return;
    const side = String(selectedCandle?.tradeSide || "LONG").toUpperCase() === "SHORT" ? "SHORT" : "LONG";
    const selector = side === "SHORT" ? "HIGH" : "LOW";
    const price = resolveLiquidityCatchCandleLevel(candle, selector, side);
    if (!Number.isFinite(price)) return;
    setPanelMode("CATCH");
    setSelectedSide(side);
    setCatchBaselineSelector(selector);
    setCatchBaselineSource("SELECTED_CANDLE");
    setCatchBaseline(formatTradePrice(price));
    setStatus(`Selected ${selectedCandle?.candle || "15M"} candle ${selector}`);
  }, [selectedCandle]);

  useEffect(() => {
    if (catchBaselineSource !== "SELECTED_CANDLE") return;
    const price = resolveLiquidityCatchCandleLevel(selectedCandle?.selectedCandle, catchBaselineSelector, selectedSide);
    if (!Number.isFinite(price)) return;
    setCatchBaseline(formatTradePrice(price));
  }, [catchBaselineSelector, catchBaselineSource, selectedCandle, selectedSide]);

  useEffect(() => {
    localStorage.setItem("catchTradeData", JSON.stringify({
      stopLossPercent: catchStopPercent,
      tp1Percent: catchTp1Percent,
      tp2Percent: catchTp2Percent,
      catchExpiry,
      entryProtectionMode: catchEntryProtectionMode
    }));
  }, [catchExpiry, catchEntryProtectionMode, catchStopPercent, catchTp1Percent, catchTp2Percent]);

  const refreshPositionData = () => {
    [0, 1500, 3000, 6000].forEach((delay) => {
      setTimeout(() => {
        mutate();
        window.dispatchEvent(new CustomEvent("position_journal_refresh", {
          detail: { symbol }
        }));
      }, delay);
    });
  };

  const sendPanelCommand = async (command, successMessage) => {
    if (!command) {
      setStatus("Command is not ready");
      throw new Error("Command is not ready");
    }
    await processCommand({ command });
    setStatus("Command accepted by App backend. Check activity for the exchange outcome.");
    refreshPositionData();
  };

  const showConfirmation = (action) => {
    if (!action.command) {
      setStatus("Command is not ready");
      return;
    }
    setConfirmation(action);
  };

  const confirmPendingAction = async () => {
    if (!confirmation) return;
    await sendPanelCommand(confirmation.command, confirmation.successMessage);
    setConfirmation(null);
  };

  const openOrAdd = (quickAmount = null) => {
    const isQuick = quickAmount != null;
    const orderAmount = isQuick ? quickAmount : amountUsdt;
    const orderPrice = isQuick ? "" : entryPrice;
    const command = buildOpenPositionCommand({
      symbol,
      side: selectedSide,
      amountUsdt: orderAmount,
      leverage,
      price: orderPrice
    });
    const actionName = selectedLane ? "add" : "open";
    showConfirmation({
      title: `Confirm ${orderPrice ? "limit" : "market"} ${actionName}`,
      confirmLabel: `Confirm ${actionName}`,
      confirmVariant: selectedSide === "LONG" ? "success" : "danger",
      side: selectedSide,
      symbol,
      rows: [
        ["Order", orderPrice ? `Limit @ ${orderPrice}` : "Market"],
        ["Margin", `${orderAmount} USDT`],
        ["Leverage", `${leverage}x`],
        ["Est. notional", `${(Number(orderAmount) * Number(leverage)).toFixed(2).replace(/\.00$/, "")} USDT`],
        ["Stop loss", "Not set"]
      ],
      notice: isQuick
        ? "This executes at market without a stop loss. Set protection after accumulating the position."
        : null,
      command,
      successMessage: `${selectedLane ? "Add" : "Open"} command sent`
    });
  };

  const reduce = (pct = reducePercent) => {
    const command = buildPositionCloseCommand({ symbol, side: selectedSide, amountPercent: pct });
    const closesAll = Number(pct) === 100;
    showConfirmation({
      title: closesAll ? "Confirm position close" : "Confirm position reduction",
      confirmLabel: closesAll ? "Close position" : "Reduce position",
      confirmVariant: closesAll ? "danger" : "warning",
      side: selectedSide,
      symbol,
      rows: [
        ["Order", "Market"],
        ["Position", positionText(selectedLane)],
        ["Amount", `${pct}%`]
      ],
      notice: closesAll ? "This closes the selected position side completely at market." : null,
      command,
      successMessage: closesAll ? "Close command sent" : "Reduce command sent"
    });
  };

  const setProtectiveOrder = (type) => {
    const price = type === "StopLoss" ? stopPrice : takePrice;
    const pct = type === "StopLoss" ? stopPercent : takePercent;
    const command = buildTpSlCommand({ symbol, side: selectedSide, type, amountPercent: pct, price });
    sendPanelCommand(command, `${type === "StopLoss" ? "SL" : "TP"} command sent`)
      .catch((error) => setStatus(error.message));
  };

  const startCatch = () => {
    const command = buildLiquidityCatchCommand({
      symbol,
      side: selectedSide,
      amountUsdt,
      leverage,
      timeframe: catchTimeframe,
      baseline: catchBaseline,
      stopLossPercent: catchStopPercent,
      tp1Percent: catchTp1Percent,
      tp2Percent: catchTp2Percent,
      expiresIn: catchExpiry,
      entryProtectionMode: catchEntryProtectionMode,
      baselineSource: catchBaselineSource,
      baselineSelector: catchBaselineSelector,
      baselineCandleTimestamp: catchBaselineSource === "SELECTED_CANDLE"
        ? liquidityCatchCandleTimestamp(selectedCandle?.selectedCandle, selectedCandle?.date) : null
    });
    const tp2Text = String(catchTp2Percent || "HOLD").toUpperCase();
    showConfirmation({
      title: `Confirm ${selectedSide === "LONG" ? "bottom" : "top"} Catch`,
      confirmLabel: "Start Catch",
      confirmVariant: selectedSide === "LONG" ? "success" : "danger",
      side: selectedSide,
      symbol,
      rows: [
        ["Baseline", `${catchBaseline || "Not set"}${catchBaselineSource === "SELECTED_CANDLE" ? ` (${catchBaselineSelector})` : ""}`],
        ["Timeframe", catchTimeframe],
        ["Attempt", `${amountUsdt} USDT x${leverage}`],
        ["Entry confirmation", catchEntryProtectionMode],
        ["Stop loss", `${catchStopPercent}%`],
        ["TP1", `${catchTp1Percent}% / 50% position`],
        ["TP2", tp2Text === "HOLD" ? "HOLD" : `${tp2Text}%`],
        ["Attempts", "Up to 3"],
        ["New entries expire", `${catchExpiry}h`]
      ],
      notice: "The process places a limit at the baseline when price comes within 1%. After a stopped attempt it waits for a confirmed new timeframe extreme.",
      command,
      successMessage: "Catch process started"
    });
  };

  if (!open) {
    return null;
  }

  return (
    <div className={mobile ? "tradingPanel tradingPanelMobile" : "tradingPanel"}>
      <div className="tradingPanelHeader">
        <div>
          <div className="tradingPanelEyebrow">Trading panel</div>
          <div className="tradingPanelSymbol">{symbol || "-"}</div>
        </div>
        <button type="button" className="tradingPanelClose" onClick={onClose}>Close</button>
      </div>

      {positionJournal && !currentPositionJournal ? (
        <div className="tradingPanelStatus" role="status">
          Exchange positions are unavailable or stale. Confirm your live exchange state before sending a trade command.
        </div>
      ) : null}

      <div className="tradingPanelModeSwitch" role="group" aria-label="Trading mode">
        {[["MANUAL", "Manual"], ["CATCH", "Catch"]].map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={panelMode === value ? "active" : ""}
            onClick={() => setPanelMode(value)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="tradingPanelSideSwitch" role="group" aria-label="Position side">
        {["LONG", "SHORT"].map((side) => (
          <button
            key={side}
            type="button"
            className={[
              side === "LONG" ? "long" : "short",
              selectedSide === side ? "active" : ""
            ].filter(Boolean).join(" ")}
            onClick={() => setSelectedSide(side)}
          >
            {side}
          </button>
        ))}
      </div>

      {panelMode === "MANUAL" ? <div className="tradingPanelSummary">
        <div>
          <span>Position</span>
          <strong>{positionText(selectedLane)}</strong>
        </div>
        {selectedLane?.unrealizedPnl != null ? (
          <div>
            <span>Unrealized</span>
            <strong className={Number(selectedLane.unrealizedPnl) >= 0 ? "positive" : "negative"}>
              {formatPnl(Number(selectedLane.unrealizedPnl))}
            </strong>
          </div>
        ) : null}
        {stopOrder ? <div><span>SL</span><strong>{formatTradePrice(activeOrderPrice(stopOrder))}</strong></div> : null}
        {takeOrder ? <div><span>TP</span><strong>{formatTradePrice(activeOrderPrice(takeOrder))}</strong></div> : null}
      </div> : null}

      {panelMode === "MANUAL" ? <>
      <section className="tradingPanelSection">
        <div className="tradingPanelSectionTitle">{selectedLane ? "Add" : "Open"}</div>
        <div className="tradingPanelChips">
          {ADD_SIZES.map((size) => (
            <button key={size} type="button" onClick={() => openOrAdd(size)}>
              {quickAddLabel(size, leverage)}
            </button>
          ))}
        </div>
        <div className="tradingPanelInputs">
          <Form.Control size="sm" value={amountUsdt} onChange={(e) => setAmountUsdt(e.target.value)} aria-label="USDT size" />
        </div>
        {!selectedLane ? (
          <div className="tradingPanelLeverage">
            <span>Leverage</span>
            <div className="tradingPanelLeverageButtons" role="group" aria-label="Leverage">
              {LEVERAGES.map((lev) => (
                <button
                  key={lev}
                  type="button"
                  className={Number(leverage) === lev ? "active" : ""}
                  onClick={() => setLeverage(lev)}
                >
                  {lev}x
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="tradingPanelLeverage tradingPanelLeverageReadonly">
            <span>Leverage</span>
            <strong>x{leverage}</strong>
          </div>
        )}
        <Form.Control size="sm" placeholder="Limit price, or blank for market (E + click)" value={entryPrice} onChange={(e) => setEntryPrice(e.target.value)} />
        <Button size="sm" variant="primary" onClick={() => openOrAdd()}>{selectedLane ? "Add" : "Open"}</Button>
      </section>

      <section className="tradingPanelSection">
        <div className="tradingPanelSectionTitle">Reduce</div>
        <div className="tradingPanelChips">
          {REDUCE_PCTS.map((pct) => (
            <button key={pct} type="button" className={Number(reducePercent) === pct ? "active" : ""} onClick={() => setReducePercent(pct)}>
              {pct}%
            </button>
          ))}
        </div>
        <div className="tradingPanelActionsRow">
          <Form.Control size="sm" value={reducePercent} onChange={(e) => setReducePercent(e.target.value)} aria-label="Reduce percent" />
          <Button size="sm" variant="outline-warning" disabled={!selectedLane} onClick={() => reduce(reducePercent)}>Reduce</Button>
          <Button size="sm" variant="outline-danger" disabled={!selectedLane} onClick={() => reduce(100)}>Close All</Button>
        </div>
      </section>

      <section className="tradingPanelSection">
        <div className="tradingPanelSectionTitle">SL / TP</div>
        <div className="tradingPanelProtectRow">
          <Form.Control size="sm" placeholder="SL price (S + click)" value={stopPrice} onChange={(e) => setStopPrice(e.target.value)} />
          <Form.Select size="sm" value={stopPercent} onChange={(e) => setStopPercent(e.target.value)} aria-label="Stop loss percent">
            {PROTECT_PCTS.map((pct) => <option key={pct} value={pct}>{pct}%</option>)}
          </Form.Select>
          <Button size="sm" variant="outline-danger" onClick={() => setProtectiveOrder("StopLoss")}>Set SL</Button>
        </div>
        <div className="tradingPanelProtectRow">
          <Form.Control size="sm" placeholder="TP price (T + click)" value={takePrice} onChange={(e) => setTakePrice(e.target.value)} />
          <Form.Select size="sm" value={takePercent} onChange={(e) => setTakePercent(e.target.value)} aria-label="Take profit percent">
            {PROTECT_PCTS.map((pct) => <option key={pct} value={pct}>{pct}%</option>)}
          </Form.Select>
          <Button size="sm" variant="outline-success" onClick={() => setProtectiveOrder("TakeProfit")}>Set TP</Button>
        </div>
      </section>

      <div className="tradingPanelHint">Hold S/T/E and click the chart to fill SL, TP, or entry price.</div>
      </> : (
        <section className="tradingPanelSection tradingPanelCatchSection">
          <div className="tradingPanelSectionTitle">Liquidity Catch</div>
          <div className="tradingPanelCatchNote">
            {selectedSide === "LONG" ? "Catch a bottom" : "Catch a top"} on {catchTimeframe}. Entry activates within 1% of the baseline.
          </div>
          <label className="tradingPanelField">
            <span>Baseline</span>
            <Form.Control
              size="sm"
              placeholder="Price (E + click)"
              value={catchBaseline}
              onChange={(e) => {
                setCatchBaseline(e.target.value);
                setCatchBaselineSource("MANUAL");
                setCatchBaselineSelector("PRICE");
              }}
            />
          </label>
          {selectedCandle?.selectedCandle ? (
            <div className="tradingPanelCandleBaseline">
              <div>
                <span>Selected candle</span>
                <strong>{selectedCandle?.candle || catchTimeframe} | {selectedCandleDateLabel(selectedCandle)}</strong>
              </div>
              <Form.Select
                size="sm"
                value={catchBaselineSelector === "PRICE" ? (selectedSide === "SHORT" ? "HIGH" : "LOW") : catchBaselineSelector}
                onChange={(e) => {
                  setCatchBaselineSelector(e.target.value);
                  setCatchBaselineSource("SELECTED_CANDLE");
                }}
                aria-label="Selected candle baseline level"
              >
                {LIQUIDITY_CATCH_CANDLE_LEVELS[selectedSide].map((level) => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </Form.Select>
            </div>
          ) : null}
          <div className="tradingPanelCatchGrid">
            <label className="tradingPanelField">
              <span>Margin per attempt</span>
              <Form.Control size="sm" value={amountUsdt} onChange={(e) => setAmountUsdt(e.target.value)} />
            </label>
            <label className="tradingPanelField">
              <span>Timeframe</span>
              <Form.Control size="sm" value={catchTimeframe} disabled />
            </label>
          </div>
          <div className="tradingPanelLeverage">
            <span>Leverage</span>
            <div className="tradingPanelLeverageButtons" role="group" aria-label="Catch leverage">
              {LEVERAGES.map((lev) => (
                <button key={lev} type="button" className={Number(leverage) === lev ? "active" : ""} onClick={() => setLeverage(lev)}>
                  {lev}x
                </button>
              ))}
            </div>
          </div>
          <EntryProtectionSelector
            value={catchEntryProtectionMode}
            onChange={setCatchEntryProtectionMode}
          />
          <div className="tradingPanelCatchGrid tradingPanelCatchRiskGrid">
            <label className="tradingPanelField">
              <span>SL %</span>
              <Form.Control size="sm" value={catchStopPercent} onChange={(e) => setCatchStopPercent(e.target.value)} />
            </label>
            <label className="tradingPanelField">
              <span>TP1 %</span>
              <Form.Control size="sm" value={catchTp1Percent} onChange={(e) => setCatchTp1Percent(e.target.value)} />
            </label>
            <label className="tradingPanelField">
              <span>TP2 % / HOLD</span>
              <Form.Control size="sm" value={catchTp2Percent} onChange={(e) => setCatchTp2Percent(e.target.value)} />
            </label>
            <label className="tradingPanelField">
              <span>Entry expiry</span>
              <Form.Select size="sm" value={catchExpiry} onChange={(e) => setCatchExpiry(e.target.value)}>
                {LIQUIDITY_CATCH_EXPIRIES.map((hours) => <option key={hours} value={hours}>{hours}h</option>)}
              </Form.Select>
            </label>
          </div>
          <Button variant={selectedSide === "LONG" ? "success" : "danger"} onClick={startCatch}>
            Start {selectedSide === "LONG" ? "Bottom" : "Top"} Catch
          </Button>
          <div className="tradingPanelHint">Hold E and click the chart to set the baseline precisely.</div>
        </section>
      )}

      {status ? <div className="tradingPanelStatus">{status}</div> : null}
      <TradeConfirmationModal
        show={Boolean(confirmation)}
        action={confirmation}
        onCancel={() => setConfirmation(null)}
        onConfirm={confirmPendingAction}
      />
    </div>
  );
}
